import { useMemo } from "react";
import type { Station } from "../lib/radio";
import { ChannelMark } from "./ChannelMark";

type Props = {
  stations: Station[];
  activeId: string | null;
  favorites: Set<string>;
  onToggleFavorite: (id: string) => void;
  onPick: (s: Station) => void;
  onClose: () => void;
};

type Section = {
  key: string;
  title: string;
  subtitle?: string;
  stations: Station[];
};

export function StationPicker({ stations, activeId, favorites, onToggleFavorite, onPick, onClose }: Props) {
  const sections: Section[] = useMemo(() => {
    const out: Section[] = [];
    const favs = stations.filter((s) => favorites.has(s.id));
    if (favs.length) out.push({ key: "favorites", title: "Favorites", subtitle: "Pinned channels", stations: favs });

    const radio = stations.filter((s) => s.kind === "radio");
    const podcasts = stations.filter((s) => s.kind === "podcast");

    // group radio by `group` field so DR sits separately from Radio Paradise / others
    const radioGroups = new Map<string, Station[]>();
    for (const s of radio) {
      if (!radioGroups.has(s.group)) radioGroups.set(s.group, []);
      radioGroups.get(s.group)!.push(s);
    }
    // DR first, then everything else alphabetically
    const orderedGroups = [...radioGroups.keys()].sort((a, b) => {
      if (a === "DR") return -1;
      if (b === "DR") return 1;
      return a.localeCompare(b);
    });
    for (const g of orderedGroups) {
      out.push({
        key: `radio-${g}`,
        title: g === "DR" ? "DR Radio" : g,
        subtitle: g === "DR" ? "Danmarks Radio · live" : "Live stream",
        stations: radioGroups.get(g)!,
      });
    }
    if (podcasts.length) {
      out.push({
        key: "podcasts",
        title: "Podcasts",
        subtitle: "Seneste afsnit afspilles",
        stations: podcasts,
      });
    }
    return out;
  }, [stations, favorites]);

  return (
    <div className="station-picker">
      <div className="station-picker-head">
        <h2>
          Choose a <em>frequency</em>
        </h2>
        <button className="close-btn" onClick={onClose}>Close</button>
      </div>
      <div className="station-sections">
        {sections.map((sec) => (
          <section key={sec.key} className="station-section">
            <header>
              <h3>{sec.title}</h3>
              {sec.subtitle && <span>{sec.subtitle}</span>}
            </header>
            <div className="station-grid">
              {sec.stations.map((s) => {
                const isFav = favorites.has(s.id);
                return (
                  <div
                    key={s.id + "@" + sec.key}
                    className={`station-card ${s.id === activeId ? "active" : ""}`}
                    style={{ "--card-accent": s.accent } as React.CSSProperties}
                  >
                    <button
                      type="button"
                      className="fav-btn"
                      onClick={(e) => { e.stopPropagation(); onToggleFavorite(s.id); }}
                      aria-label={isFav ? "Remove favorite" : "Add favorite"}
                      aria-pressed={isFav}
                    >
                      {isFav ? <StarFilled /> : <StarOutline />}
                    </button>
                    <button
                      type="button"
                      className="station-card-body"
                      onClick={() => onPick(s)}
                    >
                      {s.art
                        ? <img src={s.art} alt="" />
                        : <ChannelMark station={s} size={64} />}
                      <div className="card-meta">
                        <h4>{s.name}</h4>
                        <p>{s.tagline}</p>
                      </div>
                      <div className="card-foot">
                        <span className="card-label">
                          {s.id === activeId ? "Playing" : s.kind === "podcast" ? "Latest episode" : "Tap to tune"}
                        </span>
                        <span className="card-play" style={{ background: s.accent }}>
                          <PlayTri />
                        </span>
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function StarOutline() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polygon points="12 2 15 9 22 9.5 17 14.5 18.5 22 12 18 5.5 22 7 14.5 2 9.5 9 9 12 2" />
    </svg>
  );
}
function StarFilled() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <polygon points="12 2 15 9 22 9.5 17 14.5 18.5 22 12 18 5.5 22 7 14.5 2 9.5 9 9 12 2" />
    </svg>
  );
}
function PlayTri() {
  return (
    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M7 5.5v13c0 .8.9 1.3 1.6.9l10.5-6.5a1.1 1.1 0 0 0 0-1.8L8.6 4.6A1.1 1.1 0 0 0 7 5.5z" />
    </svg>
  );
}
